import React, { useCallback, useMemo, useState } from 'react';
import { CodeModal } from './CodeModal';
import { exportThemeToFile } from '../../../utils';
import { copyTextToClipboard } from '../../../packages/radiant-react/_utils';
import { useStyleContext } from '../../../context/StyleContext';
import { Alert } from 'packages/radiant-react/alert';

interface DownloadModalProps {
  isVisible: boolean;
  onClose: () => void;
  setMessageHandler: (message: string) => void;
}

export const DownloadModal = React.memo(
  ({ isVisible, onClose, setMessageHandler }: DownloadModalProps) => {
    const { styleState: state } = useStyleContext();
    const [internalToast, setInternalToast] = useState({ message: '', isVisible: false });

    const jsonString = useMemo(() => JSON.stringify(state, null, 2), [state]);

    const handleCopyJSON = useCallback(async (text: string | null) => {
      if (!text) return;

      try {
        // Try modern Clipboard API first (when available and permitted)
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(text);
          setInternalToast({ message: 'JSON Copied!', isVisible: true });
          setTimeout(() => setInternalToast({ message: '', isVisible: false }), 3000);
          return;
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('Clipboard API failed, using fallback method:', errorMessage);
      }

      // Fallback: use copyTextToClipboard (works without permissions)
      copyTextToClipboard(text);
      setInternalToast({ message: 'JSON Copied!', isVisible: true });
      setTimeout(() => setInternalToast({ message: '', isVisible: false }), 3000);
    }, []);

    const handleExportTheme = useCallback(() => {
      exportThemeToFile(state);
      onClose();
      setMessageHandler('JSON Exported!');
    }, [state, onClose, setMessageHandler]);

    const headerProps = useMemo(
      () => ({
        title: 'Copy JSON',
        heading: 'You can Copy or Download your styles from here.',
        onClick: () => handleCopyJSON(jsonString),
      }),
      [handleCopyJSON, jsonString]
    );

    const confirmButtonProps = useMemo(
      () => ({
        title: 'Export',
        onClick: handleExportTheme,
      }),
      [handleExportTheme]
    );

    const dismissButtonProps = useMemo(
      () => ({
        title: 'Cancel',
        onClick: onClose,
      }),
      [onClose]
    );

    const textAreaProps = useMemo(
      () => ({
        placeholder: '',
        rows: 20,
        readOnly: true,
      }),
      []
    );

    return (
      <>
        <CodeModal
          isVisible={isVisible}
          closeModal={onClose}
          headerProps={headerProps}
          confirmButtonProps={confirmButtonProps}
          dismissButtonProps={dismissButtonProps}
          content={jsonString}
          title={'Export JSON'}
          textAreaProps={textAreaProps}
          codeType="JSON"
        />
        <Alert.Toast message={internalToast.message} isVisible={internalToast.isVisible} />
      </>
    );
  }
);

DownloadModal.displayName = 'DownloadModal';
